import { BasicProgressBackgroundColors } from "@playlive/overlay-core/progress-bars/basic";
import PropTypes from "prop-types";
import Picker from "@/components/input/picker.input.jsx";
import YesNoButtonInput from "@/components/input/yes-no-button.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import GroupedColorPicker from "@/components/pickers/grouped-color.picker.jsx";
import CharitySection from "@/components/shared/charity-section.jsx";
import { charityIsTiltify, charityIsTwitch, isYes } from "@/util/common.js";

CustomizeFullProgressBar.propTypes = {
  setConfigState: PropTypes.func.isRequired,
  setConfigKeyValue: PropTypes.func.isRequired,
  showOriginalGoal: PropTypes.bool,
  overlayURL: PropTypes.string.isRequired,
  overlayName: PropTypes.string.isRequired,
  showCCAM: PropTypes.bool,
  obsDimensions: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
  }),
  config: PropTypes.shape({
    charityType: PropTypes.string,
    textColor: PropTypes.string,
    bgColor: PropTypes.string,
    showChallenges: PropTypes.oneOf(["yes", "no"]),
    showRewards: PropTypes.oneOf(["yes", "no"]),
    showDonors: PropTypes.oneOf(["yes", "no"]),
    showScroller: PropTypes.oneOf(["yes", "no"]),
    ccamMode: PropTypes.oneOf(["yes", "no"]),
    showOriginalGoal: PropTypes.oneOf(["yes", "no"]),
  }),
  children: PropTypes.node,
  childrenSlot: PropTypes.oneOf(["top", "bottom", "middle", "scroller", "appearance"]),
};

export default function CustomizeFullProgressBar({
  setConfigState,
  setConfigKeyValue,
  overlayURL,
  config,
  overlayName,
  obsDimensions,
  children,
  childrenSlot = "top",
  showCCAM = new Date().getMonth() === 8, // Show CCAM in September
}) {
  if (
    !isYes(config.showRewards) &&
    !isYes(config.showChallenges) &&
    !isYes(config.showDonors) &&
    isYes(config.showScroller)
  ) {
    setConfigState({ target: { name: "showScroller", value: "no" } });
  } else if (
    (isYes(config.showRewards) || isYes(config.showChallenges) || isYes(config.showDonors)) &&
    !isYes(config.showScroller)
  ) {
    setConfigState({ target: { name: "showScroller", value: "yes" } });
  }

  // Handles switching from Tiltify -> Twitch so that invalid options aren't shown
  if (charityIsTwitch(config.charityType)) {
    if (isYes(config.showRewards)) {
      setConfigState({ target: { name: "showRewards", value: "no" } });
    }
    if (isYes(config.showChallenges)) {
      setConfigState({ target: { name: "showChallenges", value: "no" } });
    }
  }

  const flagPositions = [
    { name: "Top Left", value: "top,left" },
    { name: "Bottom Left", value: "bottom,left" },
    { name: "Top Right", value: "top,right" },
    { name: "Bottom Right", value: "bottom,right" },
  ];

  const setFlagPosition = (event) => {
    const [vertical, horizontal] = event.target.value.split(",");
    setConfigKeyValue("ccamFlagVerticalPosition", vertical);
    setConfigKeyValue("ccamFlagHorizontalPosition", horizontal);
  };

  function filterColors(color) {
    return BasicProgressBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="mt-0 ml-auto mr-auto mb-4  px-0 w-full">
      <CharitySection onChange={setConfigState} charityType={config.charityType} />

      {childrenSlot === "top" && <div>{children}</div>}

      <CustomizeSection title="Appearance">
        <GroupedColorPicker
          disableBackgroundColor={isYes(config.ccamMode) && showCCAM}
          textColor={config.textColor}
          bgColor={config.bgColor}
          onChange={setConfigState}
          filterBG={filterColors}
        />

        <div className="my-2">
          <div className="flex flex-col md:flex-row mt-2">
            <YesNoButtonInput
              className="flex-1"
              onChange={setConfigState}
              mode="checkbox"
              currentValue={config.showOriginalGoal}
              name="showOriginalGoal"
              label="Show Original Goal"
              labelTextClassName="text-white"
              hidden={!charityIsTiltify(config.charityType)}
            />
          </div>

          <p className="pl-1 pt-1 text-base-content">
            When enabled, the progress bar will celebrate reaching your original goal and
            automatically extend to show progress toward your new goal if it's updated.
          </p>
        </div>

        {childrenSlot === "appearance" && <div>{children}</div>}
      </CustomizeSection>

      {childrenSlot === "middle" && <div>{children}</div>}

      <CustomizeSection title="Scroller" className="mb-4">
        {childrenSlot === "scroller" && <div className="mb-4">{children}</div>}

        <div className="flex flex-col md:flex-row">
          <YesNoButtonInput
            className="md:mr-4 mb-2 md:mb-0 flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showRewards}
            name="showRewards"
            label="Display Rewards"
            labelTextClassName="text-white"
            hidden={!charityIsTiltify(config.charityType)}
          />

          <YesNoButtonInput
            className="md:mr-4 mb-2 md:mb-0 flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showChallenges}
            name="showChallenges"
            label="Display Targets"
            hidden={!charityIsTiltify(config.charityType)}
          />

          <YesNoButtonInput
            className="flex-1"
            onChange={setConfigState}
            mode="checkbox"
            currentValue={config.showDonors}
            name="showDonors"
            label="Display Donors"
          />
        </div>
      </CustomizeSection>

      {showCCAM && (
        <CustomizeSection title="CCAM" className="mb-4">
          <div id="ccam">
            <YesNoButtonInput
              className="flex-1"
              onChange={setConfigState}
              mode="checkbox"
              currentValue={config.ccamMode}
              name="ccamMode"
              label="CCAM Mode"
              labelNoClass="text-black"
              buttonNoClass="bg-warning opacity-60 shadow-none"
              buttonYesClass="bg-warning opacity-100 shadow-none"
              labelYesClass="text-black"
            />

            <Picker
              disabled={!isYes(config.ccamMode)}
              onChange={setFlagPosition}
              currentValue={`${config.ccamFlagVerticalPosition},${config.ccamFlagHorizontalPosition}`}
              items={flagPositions}
              fieldName="ccamFlagPosition"
              labelText="CCAM Ribbon Position"
            />
          </div>
        </CustomizeSection>
      )}

      {childrenSlot === "bottom" && <div>{children}</div>}

      <LinkSetup
        allowTeamCampaignSelection={true}
        charityType={config.charityType}
        obsDimensions={obsDimensions}
        overlayName={overlayName}
        overlayURL={overlayURL}
      />
    </div>
  );
}
